'use client';

import React, { useState } from 'react';
import Link from '@/components/atoms/link';
import { useTranslations } from 'use-intl';
import Button from '@/components/atoms/button';
import Skeleton from '@/components/atoms/skeleton';
import EmptyState from '@/components/molecules/empty-state';
import ResultsCount from '@/components/molecules/results-count';
import ProductTileWithCt from '@/lib/tastics/product-list/components/product-tile-with-ct';
import SortBy from '@/components/molecules/sort-by';
import ViewToggle from '@/components/molecules/view-toggle';
import { ViewMode } from '@/components/molecules/view-toggle/types';
import SubcategoryNav from '@/components/molecules/subcategory-nav';
import FilterToolbar from '@/components/organisms/filter-toolbar';
import QuickFilterCheckboxes from '@/components/molecules/quick-filter-checkboxes';
import AllFiltersDrawerV2 from '@/components/organisms/all-filters-drawer-v2';
import AddToPurchaseListDrawer from '@/components/organisms/add-to-purchase-list-drawer';
import Pagination from '@/components/organisms/pagination';
import LoadingIcon from '@/components/atoms/loading-icon';
import SeoContent from '@/components/organisms/seo-content';
import SearchPageHeader from '@/components/molecules/search-page-header';
import { classnames } from '@/utils/classnames/classnames';
import { ProductListingProductEntry, ProductListingV2Props } from './types';

// EWM-185 / EWM-186 / EWM-187 / EWM-188 / EWM-189 / EWM-190
//
// Page-level composition for the redesigned Product Listing Page.
// Pure presentational — the tastic shell at lib/tastics/product-list/
// resolves Frontastic data and feeds this component. All effectful
// concerns (URL, cart writes, commercetools Shopping List writes,
// scroll-to-top, retries) are caller-owned via callbacks.
//
// Layout / colours follow the v0 mock at
// v0-electus-style-guide.vercel.app/category — primary brand colour
// from the design tokens (rgb(23 58 95)), single-row filter bar with
// the All Filters trigger on the left and the results count to the
// right, and a full-bleed gray SEO band at the bottom.
const SkeletonGrid = () => (
  <ul
    className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5"
    data-testid="skeleton-grid"
    aria-busy="true"
  >
    {Array.from({ length: 12 }).map((_, i) => (
      <li key={i} className="flex flex-col gap-3">
        <Skeleton className="aspect-square w-full rounded" />
        <Skeleton className="h-4 w-3/4" />
        <Skeleton className="h-4 w-1/2" />
      </li>
    ))}
  </ul>
);

const ProductGrid = ({
  entries,
  ctPromisesBySku,
  role,
  canAddToPurchaseList,
  backOrdersAllowed,
  viewMode,
  onAddToCart,
  onPurchaseListClick,
  onProductClick,
  onTileVisible,
}: {
  entries: ProductListingProductEntry[];
  ctPromisesBySku?: ProductListingV2Props['ctPromisesBySku'];
  role: ProductListingV2Props['role'];
  canAddToPurchaseList?: ProductListingV2Props['canAddToPurchaseList'];
  backOrdersAllowed?: boolean;
  viewMode: ViewMode;
  onAddToCart?: (productId: string, qty: number) => Promise<void>;
  onPurchaseListClick: (productId: string) => void;
  onProductClick?: (productId: string, position: number) => void;
  onTileVisible?: (productId: string, node: HTMLElement | null) => void;
}) => {
  const containerClassName =
    viewMode === 'list' ? 'flex flex-col gap-3' : 'grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5';

  return (
    <ul className={containerClassName}>
      {entries.map(({ item, brand, badge }, index) => {
        // Per-tile CT promise lookup by SKU. Each tile gets its own
        // Suspense boundary inside `ProductTileWithCt` so prices stream
        // in independently — no waiting for the slowest SKU on the page.
        const ctPromise = item.sku ? ctPromisesBySku?.[item.sku] : undefined;
        return (
          <li key={item.id} ref={onTileVisible ? (node) => onTileVisible(item.id, node) : undefined}>
            <ProductTileWithCt
              item={item}
              brand={brand}
              badge={badge}
              role={role}
              canAddToPurchaseList={canAddToPurchaseList}
              backOrdersAllowed={backOrdersAllowed}
              variant={viewMode}
              ctPromise={ctPromise}
              onAddToCart={onAddToCart ? (qty) => onAddToCart(item.id, qty) : undefined}
              onAddToPurchaseList={() => onPurchaseListClick(item.id)}
              // 1-indexed in-page position. Algolia treats each search
              // request (page) as its own queryID, so position 1 on page 2
              // is fresh — not `(page-1)*pageSize + index + 1`.
              onTileClick={onProductClick ? () => onProductClick(item.id, index + 1) : undefined}
            />
          </li>
        );
      })}
    </ul>
  );
};

const ProductListingV2 = ({
  title,
  description,
  breadcrumb,
  subcategories,
  searchHeader,
  role = 'master',
  canAddToPurchaseList,
  backOrdersAllowed = false,
  state = 'ready',
  errorType,
  query,
  products,
  ctPromisesBySku,
  totalResults,
  quickFilterPills = [],
  onQuickFilterToggle,
  filterPills,
  drawerSections,
  totalActiveFilters = 0,
  currentPage,
  totalPages,
  isRefining = false,
  seoHtml,
  seoTitle,
  purchaseLists = [],
  sortOptions,
  sortValue,
  onSortChange,
  viewMode = 'grid',
  onViewModeChange,
  onPillChange,
  onApplyFilters,
  onClearFilters,
  onPageChange,
  onAddToCart,
  onAddToPurchaseList,
  onCreatePurchaseList,
  onProductClick,
  onTileVisible,
  onRetry,
}: ProductListingV2Props) => {
  const translate = useTranslations();
  const [allFiltersOpen, setAllFiltersOpen] = useState(false);
  const [purchaseListProductId, setPurchaseListProductId] = useState<string | null>(null);

  const purchaseListProduct = products.find((p) => p.item.id === purchaseListProductId)?.item;

  const renderBody = () => {
    if (state === 'loading') return <SkeletonGrid />;

    if (state === 'error') {
      // EWM-239: branch on errorType so the body matches the AC copy.
      // `validation` (Algolia 400) gives a simplify-your-search prompt
      // with no Retry button; `network` (5xx / timeout) keeps the Retry
      // button so the user can rerun without re-typing.
      const isValidation = errorType === 'validation';
      return (
        <EmptyState
          header={isValidation ? translate('search.error-validation-header') : translate('search.error-network-header')}
        >
          <p className="text-14 mb-4 text-gray-600">
            {isValidation ? translate('search.error-validation-body') : translate('search.error-network-body')}
          </p>
          {!isValidation && onRetry && (
            <Button variant="primary" size="m" onClick={onRetry}>
              {translate('search.retry')}
            </Button>
          )}
        </EmptyState>
      );
    }

    if (state === 'no-product-selection') {
      return (
        <EmptyState header={translate('product.no-product-selection-header')}>
          <p className="text-14 text-gray-600">{translate('product.no-product-selection-body')}</p>
        </EmptyState>
      );
    }

    if (state === 'no-matches-query') {
      // EWM-242: query returned zero hits with no active filters. Distinct
      // from `no-matches` (which assumes filters are active) so the CTA
      // points the user at the catalogue root instead of clearing filters.
      return (
        <EmptyState
          header={query ? translate('search.no-results-header', { query }) : translate('search.no-results-generic')}
          className="max-w-xl"
        >
          <p className="text-14 mb-6 text-gray-600">{translate('search.no-results-subcopy')}</p>
          <Link
            href="/"
            className="bg-primary hover:bg-primary/90 text-15 inline-block min-h-[44px] rounded px-6 py-3 font-semibold text-white"
            underlineOnHover={false}
          >
            {translate('search.browse-all-cta')}
          </Link>
        </EmptyState>
      );
    }

    if (state === 'no-matches') {
      return (
        <EmptyState header="No products match your filters">
          <p className="text-14 mb-4 text-gray-600">Try adjusting your selections or clear all filters.</p>
          <Button variant="secondary" size="m" onClick={onClearFilters}>
            Clear Filters
          </Button>
        </EmptyState>
      );
    }

    if (state === 'empty-category' || products.length === 0) {
      return (
        <EmptyState header="No products found in this category">
          <p className="text-14 text-gray-600">Try another category or use search.</p>
        </EmptyState>
      );
    }

    return (
      <ProductGrid
        entries={products}
        ctPromisesBySku={ctPromisesBySku}
        role={role}
        canAddToPurchaseList={canAddToPurchaseList}
        backOrdersAllowed={backOrdersAllowed}
        viewMode={viewMode}
        onAddToCart={onAddToCart}
        onPurchaseListClick={(id) => setPurchaseListProductId(id)}
        onProductClick={onProductClick}
        onTileVisible={onTileVisible}
      />
    );
  };

  const showResultsAndPagination = state === 'ready' && products.length > 0;

  return (
    // EWM-563 — standard content frame: 1280px cap (max-w-7xl) + pt-8 heading
    // top margin, matching the cart so the layout doesn't jump on navigation.
    <div className="mx-auto w-full max-w-7xl px-4 pt-8 pb-12 lg:px-8">
      {searchHeader ? (
        // EWM-668: the unqueried /search route is now an "all products" landing,
        // so the header renders alone (no empty-state helper) above the full grid.
        <SearchPageHeader {...searchHeader} />
      ) : (
        <>
          {breadcrumb && breadcrumb.length > 0 && (
            <nav aria-label="Breadcrumb" className="mb-4">
              <ol className="text-12 flex items-center gap-2 text-gray-500" role="list">
                {breadcrumb.map((seg, i) => {
                  const isLast = i === breadcrumb.length - 1;
                  return (
                    <li key={`${seg.label}-${i}`} className="flex items-center gap-2">
                      {seg.href && !isLast ? (
                        <Link href={seg.href} underlineOnHover={false} className="hover:text-gray-700">
                          {seg.label}
                        </Link>
                      ) : (
                        <span className={classnames({ 'text-gray-700': isLast })}>{seg.label}</span>
                      )}
                      {!isLast && <span aria-hidden="true">/</span>}
                    </li>
                  );
                })}
              </ol>
            </nav>
          )}

          <div className="mb-4">
            <h1 className="text-primary text-32 font-bold">{title}</h1>
            {description && <p className="text-15 mt-1 text-gray-500">{description}</p>}
          </div>
        </>
      )}

      {/* EWM-832 — subcategory navigation, between the heading and the filter
          row. Rendered ONLY when there is at least one child to list: the
          neighbouring elements already carry their own margins (`mb-4` on the
          heading block, `pt-2` on the toolbar), so an unconditional wrapper
          would leave a visible gap on categories with no children. Sits outside
          the header branch above because that branch is shared with collection
          pages, which have no position in the category tree. */}
      {subcategories && subcategories.length > 0 && <SubcategoryNav subcategories={subcategories} />}

      <>
        {/* EWM-594: the filter/sort/view toolbar scrolls away with the page
              on every breakpoint — it previously stuck below the fixed header
              on mobile (`sticky top-[168px]`, EWM-184), which the business
              asked to remove so it doesn't occupy screen space while browsing
              a long grid. Now static everywhere. */}
        {/* EWM-768: no bg-white — the toolbar sits on the page's neutral
              (bg-gray-100) background. The opaque white was a leftover from
              when this row was sticky (EWM-184); it's static now (EWM-594),
              so the fill just read as an out-of-place white band on
              search / PLP / collections. */}
        <div className="mb-6 flex flex-wrap items-center gap-2 border-b border-gray-200 pt-2 pb-4 sm:pt-0">
          <FilterToolbar
            pills={filterPills}
            totalActiveCount={totalActiveFilters}
            onPillChange={onPillChange}
            onAllFiltersClick={() => setAllFiltersOpen(true)}
          />
          {/* EWM-686 — boolean quick filters render as checkboxes alongside the
            Brand dropdown, per the ticket ("added as a checkbox filter next to
            brands filter dropdown"). Same specs and same toggle contract as the
            EWM-185 pill molecule; only the presentation differs. */}
          <QuickFilterCheckboxes pills={quickFilterPills} onToggle={(id, next) => onQuickFilterToggle?.(id, next)} />
          {showResultsAndPagination && (
            <div className="ml-auto flex items-center gap-3">
              {/* EWM-467: on mobile the count moves to its own row below the
                    toolbar (rendered separately); here it stays inline with
                    Sort + View from `sm` up where there's room. */}
              <ResultsCount count={totalResults} className="hidden sm:block" />
              {sortOptions && sortOptions.length > 0 && (
                <SortBy options={sortOptions} value={sortValue ?? sortOptions[0].value} onChange={onSortChange} />
              )}
              {/* EWM-426: list view adds little on mobile, so the grid/list
                    toggle is hidden below `sm` (wrapper keeps ViewToggle's own
                    `inline-flex` intact — classnames has no tailwind-merge, so
                    passing `hidden` to the toggle wouldn't reliably win over its
                    base display). The persisted grid/list preference is
                    untouched, so it still applies where the toggle is shown. */}
              <div className="hidden sm:block">
                <ViewToggle value={viewMode} onChange={onViewModeChange} />
              </div>
            </div>
          )}
        </div>

        {/* EWM-467: on mobile the product count gets its own left-aligned
              row above the grid — wedged next to Sort/View in the toolbar it
              read as cramped and misaligned. Hidden from `sm` up, where the
              inline count in the toolbar takes over. */}
        {showResultsAndPagination && <ResultsCount count={totalResults} className="mb-4 sm:hidden" />}

        {/* EWM-372: while a filter refinement's SSR round-trip is in flight
              the previous grid stays mounted (React keeps it during the
              transition) — dim it and overlay a spinner so the wait is
              visible rather than feeling like a dead click. */}
        <div className="relative min-h-[200px]">
          <div
            className={classnames('transition-opacity duration-150', {
              'pointer-events-none opacity-50': isRefining,
            })}
            aria-busy={isRefining}
          >
            {renderBody()}
          </div>
          {isRefining && (
            <div
              className="absolute inset-x-0 top-0 flex justify-center pt-24"
              role="status"
              aria-label={translate('search.updating-results')}
            >
              <LoadingIcon svgWidth={40} svgHeight={40} className="fill-primary" />
            </div>
          )}
        </div>

        {/* EWM-379 / EWM-380: numbered pagination on both the category PLP
              and /search (?page=N, fixed 24/page). Replaced the search-only
              Load More, which grew `limit` past commercetools' 100-item cap
              and 404'd on the 4th click. */}
        {showResultsAndPagination && totalPages > 1 && (
          <div className="mt-10">
            <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={onPageChange} />
          </div>
        )}
      </>

      {seoHtml && (
        <div className="-mx-4 mt-16 bg-gray-100 px-4 py-12 lg:-mx-8 lg:px-8">
          <div className="mx-auto max-w-4xl">
            <SeoContent title={seoTitle} html={seoHtml} />
          </div>
        </div>
      )}

      <AllFiltersDrawerV2
        isOpen={allFiltersOpen}
        sections={drawerSections}
        onApply={(staged) => {
          onApplyFilters?.(staged);
          setAllFiltersOpen(false);
        }}
        onClear={onClearFilters}
        onClose={() => setAllFiltersOpen(false)}
      />

      {purchaseListProduct && (
        <AddToPurchaseListDrawer
          isOpen={purchaseListProductId !== null}
          product={{ name: purchaseListProduct.name, sku: purchaseListProduct.sku ?? purchaseListProduct.id }}
          lists={purchaseLists}
          onCreateList={onCreatePurchaseList}
          onSubmit={(ids) => {
            onAddToPurchaseList?.(purchaseListProduct.id, ids);
            setPurchaseListProductId(null);
          }}
          onClose={() => setPurchaseListProductId(null)}
        />
      )}
    </div>
  );
};

export default ProductListingV2;
